import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function Goals() {
  const goals = [
    {
      icon: 'ri-star-fill',
      title: 'Deliver High-Quality Services',
      description: 'We are committed to exceeding expectations by delivering services that meet the highest standards of quality, professionalism, and excellence. Every project we undertake is executed with meticulous attention to detail and a focus on achieving outstanding results.'
    },
    {
      icon: 'ri-line-chart-fill',
      title: 'Drive Business Expansion',
      description: 'Through tailored strategies and innovative solutions, we help businesses expand their reach, increase their market share, and achieve sustainable growth. Our data-driven approach ensures that every decision contributes to your long-term success.'
    },
    {
      icon: 'ri-shield-star-fill',
      title: 'Establish Trusted Partnerships',
      description: 'We aim to establish Afriq Global as a trusted partner in the digital era, building long-lasting relationships based on transparency, reliability, and mutual success. Your trust is our most valuable asset, and we work tirelessly to earn and maintain it.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Goals & Aim</h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
              Driving excellence and innovation in everything we do
            </p>
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Afriq Global's Goals</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our strategic objectives guide every decision we make and every service we deliver
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {goals.map((goal, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-700 cursor-pointer group"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 group-hover:bg-blue-700 transition-colors mx-auto">
                  <i className={`${goal.icon} text-4xl text-blue-700 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{goal.title}</h3>
                <p className="text-gray-700 leading-relaxed text-center">{goal.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aim Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-3xl p-12 md:p-16 text-center">
              <div className="w-24 h-24 flex items-center justify-center bg-black/20 rounded-full mx-auto mb-8">
                <i className="ri-focus-3-fill text-5xl text-white"></i>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">Afriq Global's Aim</h2>
              <p className="text-2xl text-gray-900 leading-relaxed font-medium">
                Empowering clients to thrive through <strong>innovation</strong>, <strong>creativity</strong>, and <strong>exceptional service</strong>
              </p>
              <div className="mt-10 pt-10 border-t-2 border-black/20">
                <p className="text-xl text-gray-900 leading-relaxed">
                  We believe that every client deserves solutions that not only meet their immediate needs but also position them for long-term success. Our aim is to be the catalyst for your growth, providing the tools, strategies, and support you need to achieve your vision and exceed your goals in an ever-evolving digital landscape.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How We Achieve Our Goals</h2>
            <p className="text-xl text-gray-600">Our approach to delivering excellence</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-700 p-8 rounded-2xl text-white text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-lightbulb-flash-fill text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Innovation</h3>
              <p className="text-sm">Embracing cutting-edge technologies and creative solutions</p>
            </div>

            <div className="bg-yellow-500 p-8 rounded-2xl text-black text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-black/20 rounded-full mx-auto mb-4">
                <i className="ri-team-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Collaboration</h3>
              <p className="text-sm">Working closely with clients as true partners</p>
            </div>

            <div className="bg-blue-700 p-8 rounded-2xl text-white text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-bar-chart-box-fill text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Results-Driven</h3>
              <p className="text-sm">Focusing on measurable outcomes and ROI</p>
            </div>

            <div className="bg-yellow-500 p-8 rounded-2xl text-black text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-black/20 rounded-full mx-auto mb-4">
                <i className="ri-customer-service-2-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Dedication</h3>
              <p className="text-sm">Committed to your success every step of the way</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Let's Achieve Your Goals Together</h2>
          <p className="text-xl text-gray-200 mb-10">
            Partner with Afriq Global and experience the difference that dedication and expertise can make
          </p>
          <Link 
            to="/contact" 
            className="inline-block px-10 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Start Your Journey
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
